import { Injectable, NgZone } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, defer, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { TokenService } from 'src/app/core/services/token.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { Logout } from 'src/app/modules/authorization/store/actions';
import { AuthorizationService } from 'src/app/modules/authorization/store/service';
import * as Sentry from '@sentry/browser';

@Injectable()
export class MainInterceptor implements HttpInterceptor {
    private headers: { [name: string]: string | string[] } = {};
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

    constructor(
        private tokenService: TokenService,
        private snackBar: MatSnackBar,
        private ngZone: NgZone,
        private store: Store,
        private router: Router,
        private authorizationService: AuthorizationService,
    ) {}

    private addHeaders(request: HttpRequest<any>): Observable<void> {
        return defer(async () => {
            if (!request.headers.get('Content-Type')) {
                if (!(request.body instanceof FormData)) {
                    this.headers['Content-Type'] = 'application/json';
                } else {
                    delete this.headers['Content-Type'];
                }
            } else {
                this.headers['Content-Type'] = request.headers.get('Content-Type');
            }
            this.headers['Cache-Control'] = 'no-cache';
            this.headers.time_zone_info = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
            const token = this.tokenService.getSessionToken();
            if (token) {
                this.headers['X-access-token'] = token;
            } else {
                delete this.headers['X-access-token'];
            }

            // payments header
            if (/payments/gm.test(request.url)) {
                let authPayment;
                let authPayemnt2;
                try {
                    authPayemnt2 = this.tokenService.getPaymentsToken();
                    authPayment =
                        (await this.tokenService.getPaymentsTokenObs
                            .pipe(
                                take(1),
                                timeout(1),
                                catchError((err) => {
                                    console.log('timeout err', err);
                                    return of(null);
                                }),
                            )
                            .toPromise()) || authPayemnt2;
                    Sentry.addBreadcrumb({
                        category: 'payroll authPayments',
                        message: 'interceptor retrieved token ' + JSON.stringify(authPayment),
                        level: 'info',
                    });
                } catch (err) {
                    Sentry.addBreadcrumb({
                        category: 'payroll authPayments',
                        message: 'interceptor retrieved failed ' + err,
                        level: 'info',
                    });
                    console.error(err);
                    authPayment = null;
                }

                if (authPayment && authPayment.token) {
                    console.log('adding payment header');
                    this.headers['X-payments-token'] = authPayment.token;
                } else {
                    console.log('removing payment header');
                    delete this.headers['X-payments-token'];
                }
            }
        });
    }

    private logout(): void {
        this.ngZone.run(() => {
            this.store.dispatch(new Logout());
            this.router.navigateByUrl('/auth');
        });
    }

    private handleRefreshToken(err: any, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const refreshToken = this.tokenService.getSessionRefreshToken();
        if (!refreshToken) {
            this.logout();
            // message about logging out
            return throwError(err);
        } else if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.authorizationService.refreshToken(refreshToken).pipe(
                switchMap(() => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(true);
                    return this.addHeaders(request).pipe(switchMap(() => next.handle(request.clone({ setHeaders: this.headers }))));
                }),
                catchError(() => {
                    this.logout();
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(false);
                    // message about logging out
                    return throwError(err);
                }),
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter((data) => data != null),
                take(1),
                switchMap((data) => {
                    if (data) {
                        return this.addHeaders(request).pipe(switchMap(() => next.handle(request.clone({ setHeaders: this.headers }))));
                    } else {
                        return throwError(err);
                    }
                }),
            );
        }
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.addHeaders(request).pipe(
            switchMap(() =>
                next.handle(request.clone({ setHeaders: this.headers })).pipe(
                    catchError((err: any) => {
                        if (err.status === 401 && !err.url.includes('/refresh-token') && this.tokenService.getSessionToken()) {
                            return this.handleRefreshToken(err, request, next);
                        } else if (err.status >= 500) {
                            this.snackBar.open('Internal server error');
                        } else {
                            if (err.error) {
                                const message = err.error.errors && err.error.errors.length && err.error.errors[0].message;
                                this.snackBar.open(message || 'Unknown error');
                            }
                        }
                        return throwError(err);
                    }),
                ),
            ),
        );
    }
}
